import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// @ts-expect-error these exist in each respective front end project
// eslint-disable-next-line import/no-unresolved
import composeUrl from 'app/config/compose-url';

import {
  Button,
  Row,
  Col,
  Collapse,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from 'reactstrap';

import _ from 'lodash';

import Link from 'next/link';
import domainModel from 'domain-model';
import type { Cart, CartState } from 'domain-model/types/pcs';
import type { OrderFullfillmentCenterReference } from 'domain-model/types/oms';
import type { ShopConfig } from 'domain-model/types/supplier-gateway';
import {
  resetCart as _resetCart,
  removeCartRow as _removeCartRow,
} from '../../actions/persisted-multi-cart.actions';
import Money from '../Money';
import CartRow from './CartRow';
import IconX from '../icons/IconX';
import { confirm } from '../Confirmation';
import StyledButton from '../StyledButton';
import MultiCartSummary from './MultiCartSummary';

import type { PersistedMultiCartState } from '../../reducers/persisted-multi-cart.reducer';

const getDisabledCartState = (cartState?: CartState) => {
  if (cartState === 'building' || !cartState) {
    return false;
  }
  return true;
};

const CartCollapse = ({
  cart,
  removeCartRow,
  onLinkClick,
  isTheOnlyCart,
  isGothiaMode = false,
}: {
  cart: Cart;
  removeCartRow: Function;
  onLinkClick: Function;
  isTheOnlyCart: boolean;
  isGothiaMode?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const isDisabledCart = getDisabledCartState(cart.cartState);
  const cartKey = domainModel.cart.getCartKey(cart);

  if (isTheOnlyCart) {
    return (
      <div key="cartCollapse">
        {cart.cartRows.map(cartRow => (
          <CartRow
            explicitCartKey={cartKey}
            cartRow={cartRow}
            key={cartRow.id}
            handleRemoveItem={() => {
              removeCartRow(cartRow, cart);
            }}
            onLinkClick={() => onLinkClick(cartRow)}
            disableQuantityInput={isDisabledCart}
            isGothiaMode={isGothiaMode}
          />
        ))}
      </div>
    );
  }

  return (
    <div key="cartCollapse">
      {cart.cartRows[0] && (
        <div>
          <CartRow
            explicitCartKey={cartKey}
            cartRow={cart.cartRows[0]}
            key={cart.cartRows[0].id}
            handleRemoveItem={() => {
              removeCartRow(cart.cartRows[0], cart);
            }}
            onLinkClick={() => onLinkClick(cart.cartRows[0])}
            disableQuantityInput={isDisabledCart}
            isGothiaMode={isGothiaMode}
          />
        </div>
      )}
      <Collapse isOpen={isOpen} key={`collapse_${cart.id}`}>
        {_.drop(cart.cartRows).map(cartRow => (
          <CartRow
            explicitCartKey={cartKey}
            cartRow={cartRow}
            key={cartRow.id}
            handleRemoveItem={() => {
              removeCartRow(cartRow, cart);
            }}
            disableQuantityInput={isDisabledCart}
            onLinkClick={() => onLinkClick(cartRow)}
            isGothiaMode={isGothiaMode}
          />
        ))}
      </Collapse>
      {cart.cartRows.length > 1 && (
        <div className="text-center" key="collapseButton">
          <Button color="none" className="w-100" onClick={toggle}>
            {!isOpen && <div>{cart.cartRows.length - 1} till</div>}
            <FontAwesomeIcon
              size="lg"
              icon={isOpen ? faChevronUp : faChevronDown}
            />
          </Button>
        </div>
      )}
    </div>
  );
};

CartCollapse.propTypes = {
  cart: PropTypes.object.isRequired,
  removeCartRow: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func,
};

function CartOffCanvas({
  isOpen,
  toggle,
  persistedMultiCart,
  resetCart,
  removeCartRow,
  shopConfig,
  isGothiaMode = false,
}: {
  isOpen: boolean;
  toggle: any;
  persistedMultiCart: PersistedMultiCartState;
  resetCart: Function;
  removeCartRow: Function;
  shopConfig: ShopConfig;
  isGothiaMode: boolean;
}) {
  const { carts } = persistedMultiCart;

  const cartsWithRows = _.filter(carts, cart => cart.cartRows.length > 0);
  const orderRequirements = _.map(cartsWithRows, cart =>
    domainModel.cart.getMinimumOrderRequirements(cart, shopConfig),
  );

  const hasOrderBelowMinimumOrderValue = _.find(
    orderRequirements,
    orderRequirement => !orderRequirement.isMinimumValueSatisfied,
  );
  const hasOrderThatNeedsToAcceptFrieghtPaymentCharges = _.find(
    orderRequirements,
    x =>
      x.isPossibleToPlaceOrderBelowOrderMinimum && !x.isMinimumValueSatisfied,
  );
  const totalAmountOfOrders = _.keys(cartsWithRows).length;

  const confirmReset = async () => {
    if (await confirm('Är du säker på att du vill tömma varukorgen?')) {
      resetCart();
    }
  };

  return (
    <Offcanvas
      id="cartModal"
      key="off-canvas-{isOpen ? 'open' : 'closed'}"
      direction="end"
      role="dialog"
      toggle={toggle}
      isOpen={isOpen}
      style={{ zIndex: '1100', width: '650px' }}
    >
      <OffcanvasHeader className="border-0 justify-content-end w-100">
        <Button
          onClick={toggle}
          aria-label="Close"
          className="close-rotate"
          color="none"
        >
          <IconX height="20" />
        </Button>
      </OffcanvasHeader>
      <OffcanvasBody key="off-canvas-body" className="pt-0">
        {_.map(cartsWithRows, cart => {
          const numberOfArticlesOnOrder = cart.cartRows.length;
          const orderFullfillmentCenter =
            cart.orderFullfillmentCenter as OrderFullfillmentCenterReference;
          const cartState = cart.cartState;
          const externalMarketplaceURL = cart.externalMarketplaceURL as
            | string
            | undefined;

          return (
            <Row className="pb-3" key={cart.id}>
              <Col>
                <hr className="mt-0" />
                <Row key="listHeader">
                  <Col>
                    <h4>
                      {totalAmountOfOrders > 1
                        ? orderFullfillmentCenter?.name
                        : 'Varukorgen'}
                    </h4>
                  </Col>

                  {!isGothiaMode && cartState === 'building' && (
                    <Col className="text-end">
                      <Button color="none" onClick={toggle} className="p-0 ">
                        <Link
                          href={composeUrl('articleSearch', undefined, {
                            '$articleBatches.orderFullfillmentCenterId$':
                              orderFullfillmentCenter?.id,
                          })}
                        >
                          SE SORTIMENT
                        </Link>
                      </Button>
                    </Col>
                  )}
                  {!isGothiaMode &&
                    cartState === 'readyForPurchaseInExternalSystem' &&
                    externalMarketplaceURL && (
                      <Col className="text-end">
                        <Button color="none" onClick={toggle} className="p-0 ">
                          <Link
                            href={
                              externalMarketplaceURL.startsWith('http')
                                ? externalMarketplaceURL
                                : `https://${externalMarketplaceURL}`
                            }
                            passHref
                          >
                            Gå till varukorg på extern marknadsplats
                          </Link>
                        </Button>
                      </Col>
                    )}
                </Row>

                <Row key="numRowsSummary">
                  <Col>Antal rader: {numberOfArticlesOnOrder} st</Col>
                  <Col className="text-end">
                    <Money
                      currency={cart.currency}
                      value={domainModel.cart.getCartTotal(cart)}
                    ></Money>
                  </Col>
                </Row>
                <CartCollapse
                  removeCartRow={removeCartRow}
                  cart={cart}
                  onLinkClick={toggle}
                  isTheOnlyCart={totalAmountOfOrders === 1}
                  isGothiaMode={isGothiaMode}
                />
              </Col>
            </Row>
          );
        })}
        {_.isEmpty(cartsWithRows) && (
          <Row key="emptyCart">
            <Col className="text-center">Varukorgen är tom</Col>
          </Row>
        )}
      </OffcanvasBody>
      {!_.isEmpty(cartsWithRows) && (
        <Row className="px-3">
          <Col className=" pb-2" key="summaryColumn">
            <hr className="mt-0" />
            <MultiCartSummary carts={carts} />
            <Row className="mb-4" key="summary">
              {hasOrderBelowMinimumOrderValue &&
                !hasOrderThatNeedsToAcceptFrieghtPaymentCharges && (
                  <Col className="text-danger text-center">
                    Det finns order som inte uppfyller minimumkraven
                  </Col>
                )}
              {!hasOrderBelowMinimumOrderValue &&
                hasOrderThatNeedsToAcceptFrieghtPaymentCharges && (
                  <Col className="text-warning text-center">
                    Det finns order där fraktkostnad kan tillkomma
                  </Col>
                )}
              <Col xs={12}>
                <Link href="/app/checkout" className="text-decoration-none">
                  <StyledButton
                    id="toCheckout"
                    onClick={() => toggle()}
                    color="primary"
                    block
                  >
                    Till kassan
                  </StyledButton>
                </Link>
              </Col>
              <Col className="text-center mt-4">
                <Button
                  id="emptyCartButton"
                  onClick={confirmReset}
                  size="sm"
                  color="none"
                  className="text-decoration-none text-muted"
                >
                  Töm varukorgen
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </Offcanvas>
  );
}

const mapStateToProps = ({
  persistedMultiCart,
  shopConfig,
}: {
  persistedMultiCart: PersistedMultiCartState;
  shopConfig: ShopConfig;
}) => ({
  persistedMultiCart,
  shopConfig,
});

const mapDispatchToProps = {
  resetCart: _resetCart,
  removeCartRow: _removeCartRow,
};

export default connect(mapStateToProps, mapDispatchToProps)(CartOffCanvas);
